import { useEffect, useRef } from "react";
import { useTheme } from "@/hooks/useTheme";

export default function HeroSection() {
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = [
      { r: 232, g: 118, b: 26 },
      { r: 245, g: 200, b: 66 },
      { r: 90, g: 123, b: 168 },
    ];

    const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number; colorIdx: number }[] = [];
    for (let i = 0; i < 90; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: isDark ? Math.random() * 1.5 + 0.5 : Math.random() * 2 + 1,
        alpha: isDark ? Math.random() * 0.5 + 0.15 : Math.random() * 0.55 + 0.25,
        colorIdx: Math.floor(Math.random() * colors.length),
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        const c = colors[p.colorIdx];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            const c = colors[particles[i].colorIdx];
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},${(isDark ? 0.06 : 0.18) * (1 - dist / 120)})`;
            ctx.lineWidth = isDark ? 0.5 : 0.8;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", handleResize);
    };
  }, [isDark]);

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const subColor = isDark ? "rgba(255,255,255,0.55)" : "rgba(28,26,34,0.55)";
  const mutedColor = isDark ? "rgba(255,255,255,0.28)" : "rgba(28,26,34,0.38)";
  const statBorder = isDark ? "rgba(255,255,255,0.05)" : "rgba(28,26,34,0.08)";
  const statDivider = isDark ? "rgba(255,255,255,0.05)" : "rgba(28,26,34,0.08)";
  const statBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(28,26,34,0.02)";
  const eyebrowBg = isDark ? "rgba(232,118,26,0.08)" : "rgba(232,118,26,0.1)";
  const eyebrowBorder = isDark ? "rgba(232,118,26,0.25)" : "rgba(232,118,26,0.35)";
  const outlineBtnBorder = isDark ? "rgba(255,255,255,0.15)" : "rgba(28,26,34,0.2)";
  const outlineBtnColor = isDark ? "white" : "#1C1A22";

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden transition-colors duration-500"
      style={{ background: bg }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Radial glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{ background: isDark ? "radial-gradient(circle, rgba(232,118,26,0.07) 0%, transparent 65%)" : "radial-gradient(circle, rgba(232,118,26,0.09) 0%, transparent 65%)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{ background: isDark ? "radial-gradient(circle, rgba(245,200,66,0.05) 0%, transparent 65%)" : "radial-gradient(circle, rgba(245,200,66,0.08) 0%, transparent 65%)" }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-[1px]"
          style={{ background: "linear-gradient(90deg, transparent, rgba(232,118,26,0.4), rgba(245,200,66,0.3), transparent)" }}
        />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 pt-32 pb-24 text-center">
        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full mb-10 transition-all duration-500"
          style={{ background: eyebrowBg, border: `1px solid ${eyebrowBorder}` }}
        >
          <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background: "#E8761A" }}></span>
          <span className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: "#E8761A" }}>AI-Powered Business Integration</span>
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-thin leading-[1.05] tracking-tight mb-6 transition-colors duration-500" style={{ color: headlineColor }}>
          Where Systems
          <br />
          <span
            className="font-semibold transition-colors duration-500"
            style={{ color: headlineColor }}
          >
            Connect &amp; Revenue
          </span>
          <br />
          <span
            className="font-semibold"
            style={{
              background: "linear-gradient(135deg, #E8761A 0%, #F5C842 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Flows.
          </span>
        </h1>

        <p className="text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed font-light transition-colors duration-500" style={{ color: subColor }}>
          Most businesses don&apos;t have a lead problem.
          <br />
          They have a <span style={{ color: headlineColor }} className="font-normal">systems problem.</span>
        </p>
        <p className="text-sm md:text-base max-w-xl mx-auto mb-12 leading-relaxed transition-colors duration-500" style={{ color: mutedColor }}>
          Missed calls. Disconnected tools. Delayed follow-ups. Broken workflows. All of it leads to one thing: lost revenue.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <button
            onClick={() => scrollTo("book-audit")}
            className="px-8 py-3.5 rounded-full text-white font-semibold text-[15px] transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #E8761A, #F5C842)" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.9")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
          >
            Book Your System Audit
          </button>
          <button
            onClick={() => scrollTo("industries")}
            className="px-8 py-3.5 rounded-full font-light text-[15px] transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
            style={{ border: `1px solid ${outlineBtnBorder}`, color: outlineBtnColor }}
          >
            See Industries
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </button>
        </div>

        {/* Stats */}
        <div
          className="flex flex-wrap justify-center gap-px rounded-2xl overflow-hidden transition-all duration-500"
          style={{ border: `1px solid ${statBorder}` }}
        >
          {[
            { value: "35%", label: "More Booked Jobs" },
            { value: "Real-Time", label: "Response Automation" },
            { value: "Zero", label: "Missed Opportunities" },
            { value: "5+", label: "Industries Served" },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`flex-1 min-w-[140px] px-8 py-6 text-center ${i < 3 ? "border-r" : ""}`}
              style={{ background: statBg, borderColor: statDivider }}
            >
              <div
                className="text-2xl md:text-3xl font-semibold mb-1"
                style={{
                  background: "linear-gradient(135deg, #E8761A, #F5C842)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {stat.value}
              </div>
              <div className="text-xs uppercase tracking-widest transition-colors duration-500" style={{ color: mutedColor }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div
          className="w-[1px] h-12 animate-pulse"
          style={{ background: "linear-gradient(to bottom, rgba(232,118,26,0.5), transparent)" }}
        />
      </div>
    </section>
  );
}
